import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientWholesalerCategoryEntity } from './client-wholesaler-category.entity';
import { ClientEntity } from './client.entity';
import { WholesalerEntity } from './wholesaler.entity';

/**
 * A customer's relationship with a competitor (SM1: the customer's
 * wholesaler). One row per customer and competitor: the pair is unique, and
 * the Mover integration's Pharmsource lead import upserts on it without
 * touching any other column.
 *
 * SM1 exposure (PD-17066): SM1's client search index (supplymover
 * `migration/20230101000000005-PD-10057-init-si-client.json`) joins this table
 * twice, as `primaryWholesaler` and `secondaryWholesaler`, reading `_id`,
 * `_fk_wholesaler__id` and `tier` with no filter on `tier`. SM1 has no screen
 * that writes it, and its `wholesalersPrimary` / `wholesalersSecondary`
 * display fields (`src/model/objects/client-details-formatted.model.ts`) are
 * declared but never filled. `tier` is a free-text field in SM1's object
 * metadata. So the two tier codes SM2 adds ('3' also buys from, '4' former)
 * are carried by SM1 as text and never interpreted. SM1 and the legacy object
 * API HARD-delete rows here, and SM2 does the same rather than relying on
 * `_deleted_date`.
 *
 * Every column added for SM2 is nullable, so SM1's and the integration's
 * inserts keep working.
 */
@Index('client_wholesaler__fk_wholesaler__id_fk', ['fkWholesalerId'], {})
@Index('client_wholesaler_pk', ['fkClientId', 'fkWholesalerId'], {
	unique: true,
})
@Entity('client_wholesaler')
export class ClientWholesalerEntity extends BaseEntity {
	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: '_fk_wholesaler__id', unsigned: true })
	public fkWholesalerId!: number;

	/**
	 * SM1's tier code, kept in step with `relationship`: '1' primary, '2'
	 * secondary, '3' also buys from, '4' former
	 * (`COMPETITOR_TIER_BY_RELATIONSHIP`). Rows written by SM1 or an import
	 * carry only this, so SM2 derives the relationship from it whenever it is a
	 * known code (`resolveCompetitorRelationship`).
	 */
	@Column('varchar', { name: 'tier', nullable: false, length: 255 })
	public tier!: string;

	/** 'primary' | 'secondary' | 'also_buys' | 'former'; NULL on rows SM2 never wrote. */
	@Column('varchar', { name: 'relationship', nullable: true, length: 32 })
	public relationship!: string | null;

	@Column('decimal', {
		name: 'estimatedAnnualSpend',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public estimatedAnnualSpend!: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	/** When someone last confirmed this relationship is still true. */
	@Column('datetime', { name: 'confirmedDate', nullable: true })
	public confirmedDate!: Date | null;

	/** Tenant user id (`user._id`) of whoever confirmed it. */
	@Column('int', {
		name: 'fk_user__id_confirmedBy',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdConfirmedBy!: number | null;

	@ManyToOne(() => ClientEntity, (client) => client.clientWholesalers, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(
		() => WholesalerEntity,
		(wholesaler) => wholesaler.clientWholesalers,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: '_fk_wholesaler__id', referencedColumnName: 'id' }])
	public fkWholesaler!: WholesalerEntity;

	@OneToMany(
		() => ClientWholesalerCategoryEntity,
		(category) => category.clientWholesaler,
		{},
	)
	public categories!: Array<ClientWholesalerCategoryEntity>;
}
